import React, { useEffect, useState } from "react";
import * as notify from "../../shared/notify";
import { useSidePanel } from "shared/hooks/use-side-panel";
import StatusItem from "./components/status-item";
import { addRedoTask } from "../final-qa/lib/finalData";
import { checkRedoEligible, shouldShowImageApprovalNote, showRedo, taskIcons } from "./lib/constants";
import { sendNote } from "../notes/lib/data";
import { SECTION_MAP } from "../task-list/lib/constants";

import "./task-status.scss";
import { uniq } from "lodash";

window.refreshTimer = null;

const TaskStatus = ({ sourcePage, ...props }) => {
    const [isBusy, setIsBusy] = useState(false);
    const [tasks, setTasks] = useState([]);
    const { isSalesSample } = useSidePanel();

    useEffect(() => {
        return () => {
            clearTimeout(window.refreshTimer);
        };
    }, []);

    useEffect(() => {
        async function fetchData() {
            const availableTaskTypes = uniq(props.tasks.map((t) => t.taskType.split(".")[0]));
            const useTaskIcons = taskIcons.reduce((arr, i) => {
                if (availableTaskTypes.includes(i.taskType)) {
                    arr.push(i);
                }

                return arr;
            }, []);

            // Incomplete Task Types
            const incompleteTaskTypes = props.tasks.filter((i) => !i.isCompleted).map((t) => t.taskType.split(".")[0]);

            const newTasks = useTaskIcons.map((t) => {
                return {
                    ...t,
                    tasks: props.tasks
                        .filter((task) => task.taskType.split(".")[0] === t.taskType)
                        .sort((a, b) => new Date(b.taskCreatedOn) - new Date(a.taskCreatedOn)),

                    isRedoEligible: checkRedoEligible(t.taskType.split(".")[0], incompleteTaskTypes),
                    showRedo: props.isFinalized ? false : showRedo(t.taskType),
                    isComplete: !incompleteTaskTypes.includes(t.taskType),
                };
            });

            setTasks(newTasks);
        }

        if (props.tasks.length > 0) {
            fetchData();
        }
    }, [props.tasks]);

    const handleRedoClick = async (taskType, shipmentKey, shipmentProductKey, isRedoEligible) => {
        if (!isRedoEligible) {
            notify.error("All related tasks must be completed before this task is redo eligible");

            return;
        }

        if (!shipmentKey || !shipmentProductKey) {
            notify.error("Missing ShipmentKey and/or ShipmentProductKey.");
        }

        const shortTaskType = taskType.split(".")[0];

        if (props.imagesNeedClientApproval && shouldShowImageApprovalNote(shortTaskType)) {
            const imageApprovalConfirm = await notify.confirm(
                "Image approval will be required for this product.<br /><br />Are you sure you want to reject?"
            );

            if (!imageApprovalConfirm) {
                return;
            }
        }

        setIsBusy(true);

        const prompt = await notify.prompt("Reason for reject:");

        if (!prompt) {
            if (typeof prompt === "string") {
                notify.error("Reject reason required");
            }

            setIsBusy(false);

            return;
        }

        setIsBusy(false);

        const noteTaskType = SECTION_MAP[shortTaskType].noteKey;
        const rejectComment = await sendNote(
            {
                items: [
                    {
                        noteText: prompt,
                        noteType: "Product",
                        shipmentKey: shipmentKey,
                        shipmentProductKey: shipmentProductKey,
                        taskType: noteTaskType,
                    },
                ],
            },
            sourcePage
        );

        if (!rejectComment) {
            notify.error("Task comment did not save!");
        }

        addRedoTask(props.contextKey, shortTaskType, sourcePage).then((response) => {
            if (response && response.length > 0) {
                notify.error(response.join(", "));
            } else {
                notify.success("Task Created");
            }

            window.refreshTimer = setTimeout(props.refreshTaskList(), 500);
        });
    };

    const headers = tasks.map((task) => {
        const isCurrentTask = props.currentTask && task.taskType === props.currentTask.split(".")[0] ? true : false;
        const redoTask = task.taskType.split(".")[0];
        const profilingOverride = redoTask === "scheduling" ? "profiling" : redoTask;
        const disableRedo = props.hasReadOnlyPermission && props.hasReadOnlyPermission(`redo:${profilingOverride}from${sourcePage}`);
        const disableCodingRedo = (isSalesSample || props.isSalesSample) && redoTask === "codingqa";

        return (
            <StatusItem
                key={`header-${task.taskType}`}
                task={task}
                isBusy={isBusy}
                handleRedoClick={handleRedoClick}
                isCurrentTask={isCurrentTask}
                disabled={disableRedo || disableCodingRedo}
            />
        );
    });

    return <div className="task-status">{headers}</div>;
};

export default TaskStatus;
